import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
    FaClipboardList, FaPhoneAlt, FaStore, FaUserShield,
    FaCheckCircle, FaClock, FaChevronLeft, FaChevronRight, FaCalendarDay,
    FaMapMarkerAlt, FaAngleDoubleRight, FaAngleDoubleLeft, FaSort, FaTasks,
    FaListUl,
    FaTable,
} from "react-icons/fa";

const SORT_OPTIONS = [
    { value: "createdAt:desc", label: "تاريخ الشكوى تنازلي", dateField: "createdAt", sortDirection: "desc" },
    { value: "createdAt:asc", label: "تاريخ الشكوى تصاعدي", dateField: "createdAt", sortDirection: "asc" },
    { value: "processedAt:desc", label: "تاريخ معالجة الشكوى تنازلي", dateField: "processedAt", sortDirection: "desc" },
    { value: "processedAt:asc", label: "تاريخ معالجة الشكوى تصاعدي", dateField: "processedAt", sortDirection: "asc" },
];

const DEFAULT_SORT = "createdAt:desc";

const parseSortValue = (value) => {
    const found = SORT_OPTIONS.find((o) => o.value === value);
    return found || SORT_OPTIONS[0];
};

function buildPaginationItems(currentPage, totalPages, siblingCount = 2, boundaryCount = 1) {
    if (totalPages < 1) return [];
    if (totalPages <= 10) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const set = new Set();
    for (let i = 1; i <= Math.min(boundaryCount, totalPages); i += 1) set.add(i);
    for (let i = Math.max(1, totalPages - boundaryCount + 1); i <= totalPages; i += 1) set.add(i);
    for (let i = currentPage - siblingCount; i <= currentPage + siblingCount; i += 1) {
        if (i >= 1 && i <= totalPages) set.add(i);
    }
    const sorted = Array.from(set).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const p of sorted) {
        if (prev && p - prev > 1) out.push("ellipsis");
        out.push(p);
        prev = p;
    }
    return out;
};

const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("ar-SY");
};

const normalizePhoneDigits = (value) => {
    const arabic = "٠١٢٣٤٥٦٧٨٩";
    const eastern = "۰۱۲۳۴۵۶۷۸۹";
    let s = String(value ?? "");
    s = s.replace(/[٠-٩]/g, (d) => String(arabic.indexOf(d)));
    s = s.replace(/[۰-۹]/g, (d) => String(eastern.indexOf(d)));
    return s.replace(/\D/g, "").slice(0, 10);
};

const getActivePhoneFilter = (value) => {
    const digits = normalizePhoneDigits(value);
    return digits.length === 10 ? digits : "";
};

export default function ComplaintsRegistryPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { backEnd_request, governorates, getUserConstraints } = BackMethods();
    const { userGovernorate, isSuperAdmin } = getUserConstraints();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [complaints, setComplaints] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [counts, setCounts] = useState({ all: 0, processed: 0, unprocessed: 0 });
    const [jumpToPage, setJumpToPage] = useState("");

    const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "all");
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1", 10));
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get("pageSize") || "20", 10));
    const [sortValue, setSortValue] = useState(searchParams.get("sort") || DEFAULT_SORT);
    const [governorateFilter, setGovernorateFilter] = useState(() => {
        const fromUrl = searchParams.get("governorate");
        if (fromUrl) return fromUrl;
        return isSuperAdmin ? "" : userGovernorate || "";
    });
    const [phoneFilter, setPhoneFilter] = useState(() => normalizePhoneDigits(searchParams.get("phone") || ""));
    const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
    const [toDate, setToDate] = useState(searchParams.get("to") || "");

    const sortConfig = useMemo(() => parseSortValue(sortValue), [sortValue]);
    const activePhoneFilter = useMemo(() => getActivePhoneFilter(phoneFilter), [phoneFilter]);

    const truncateWords = (text, limit) => {
        if (!text) return "---";
        const words = text.split(" ");
        return words.length > limit ? `${words.slice(0, limit).join(" ")}...` : text;
    };

    const fetchComplaints = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                pageSize,
                take: pageSize,
                dateField: sortConfig.dateField,
                sortDirection: sortConfig.sortDirection,
            };

            if (governorateFilter) params.governorate = governorateFilter;
            if (activePhoneFilter) params.phoneNumber = activePhoneFilter;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;

            if (activeTab === "processed") params.isProcessed = true;
            else if (activeTab === "unprocessed") params.isProcessed = false;

            const response = await backEnd_request.get("/public/staff/complaints", { params });
            const data = response.data || {};
            setComplaints(data.complaints || []);
            setTotalCount(data.total || 0);
            setCounts({
                all: data.counts?.all ?? 0,
                processed: data.counts?.processed ?? 0,
                unprocessed: data.counts?.unprocessed ?? 0,
            });
        } catch (err) {
            console.error("Error:", err);
            setComplaints([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [
        activeTab,
        currentPage,
        pageSize,
        governorateFilter,
        activePhoneFilter,
        fromDate,
        toDate,
        sortConfig,
        backEnd_request,
    ]);

    useEffect(() => {
        fetchComplaints();
    }, [fetchComplaints]);

    useEffect(() => {
        const params = {
            tab: activeTab,
            page: String(currentPage),
            pageSize: String(pageSize),
            sort: sortValue,
        };
        if (governorateFilter) params.governorate = governorateFilter;
        if (activePhoneFilter) params.phone = activePhoneFilter;
        if (fromDate) params.from = fromDate;
        if (toDate) params.to = toDate;
        setSearchParams(params, { replace: true });
    }, [
        activeTab,
        currentPage,
        pageSize,
        governorateFilter,
        activePhoneFilter,
        fromDate,
        toDate,
        sortValue,
        setSearchParams,
    ]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const resetPage = () => setCurrentPage(1);

    const handlePhoneChange = (value) => {
        const digits = normalizePhoneDigits(value);
        const wasActive = getActivePhoneFilter(phoneFilter);
        const willBeActive = getActivePhoneFilter(digits);
        setPhoneFilter(digits);
        if (wasActive !== willBeActive || (willBeActive && digits !== phoneFilter)) {
            resetPage();
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize) || 1;

    const paginationItems = useMemo(
        () => buildPaginationItems(currentPage, totalPages),
        [currentPage, totalPages]
    );

    const goToPage = (page) => {
        const p = Math.max(1, Math.min(page, totalPages));
        setCurrentPage(p);
    };

    const handleJumpToPage = (e) => {
        e.preventDefault();
        const p = parseInt(jumpToPage, 10);
        if (!Number.isNaN(p)) goToPage(p);
        setJumpToPage("");
    };

    const tabCount = (tab) => {
        if (tab === "all") return counts.all;
        if (tab === "processed") return counts.processed;
        return counts.unprocessed;
    };

    return (
        <div className="complaints-wrapper bg-light min-vh-100 py-3 py-md-4" dir="rtl">
            <div className="container-fluid px-1 px-md-3">
                <div className="registry-top-bar d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-3 mb-3 mb-md-4 bg-white p-3 rounded-4 shadow-sm border-bottom border-primary border-3">
                    <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3 flex-grow-1 min-w-0">
                        <div className="d-flex align-items-center gap-2 min-w-0">
                            <div className="bg-primary text-white p-2 rounded-3 d-sm-flex d-none flex-shrink-0">
                                <FaListUl size={18} />
                            </div>
                            <div className="min-w-0">
                                <h1 className="fw-bold text-dark m-0 fs-6 fs-md-5 text-truncate">سجل الشكاوي الشامل</h1>
                                <span className="badge bg-primary-subtle text-primary rounded-pill px-2 small">
                                    النتائج: {totalCount}
                                </span>
                            </div>
                        </div>

                        <div className="header-status-tabs d-flex align-items-center gap-1 flex-shrink-0">
                            <button
                                type="button"
                                className={`header-status-btn ${activeTab === "all" ? "active-all" : ""}`}
                                onClick={() => handleTabChange("all")}
                                title="كل الشكاوي"
                            >
                                <FaClipboardList size={10} />
                                <span>الكل</span>
                                <em>{tabCount("all")}</em>
                            </button>
                            <button
                                type="button"
                                className={`header-status-btn ${activeTab === "unprocessed" ? "active-unprocessed" : ""}`}
                                onClick={() => handleTabChange("unprocessed")}
                                title="قيد الانتظار"
                            >
                                <FaClock size={10} />
                                <span className="d-none d-xl-inline">قيد الانتظار</span>
                                <span className="d-xl-none">انتظار</span>
                                <em>{tabCount("unprocessed")}</em>
                            </button>
                            <button
                                type="button"
                                className={`header-status-btn ${activeTab === "processed" ? "active-processed" : ""}`}
                                onClick={() => handleTabChange("processed")}
                                title="معالجة"
                            >
                                <FaCheckCircle size={10} />
                                <span>معالجة</span>
                                <em>{tabCount("processed")}</em>
                            </button>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 align-self-md-center flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => {
                                const params = new URLSearchParams({ autoLoad: "1" });
                                if (fromDate) params.set("from", fromDate);
                                if (toDate) params.set("to", toDate);
                                navigate(`/admin/complaints-matrix-report?${params.toString()}`);
                            }}
                            className="btn btn-outline-primary rounded-pill px-3 btn-sm fw-bold"
                        >
                            <FaTable className="ms-1" />
                            تقرير مصفوفي
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/admin")}
                            className="btn btn-outline-dark rounded-pill px-3 btn-sm fw-bold"
                        >
                            رجوع
                        </button>
                    </div>
                </div>

                <div className="filters-panel bg-white shadow-sm rounded-4 p-3 mb-4 border">
                    <div className="row g-2 g-md-3">
                        <div className="col-12 col-md-6 col-lg-3">
                            <label className="filter-label">
                                <FaMapMarkerAlt className="ms-1 text-primary" />
                                المحافظة
                            </label>
                            <select
                                className="form-select"
                                value={governorateFilter}
                                disabled={!isSuperAdmin && !!userGovernorate}
                                onChange={(e) => {
                                    setGovernorateFilter(e.target.value);
                                    resetPage();
                                }}
                            >
                                <option value="">كل المحافظات</option>
                                {governorates.map((g) => (
                                    <option key={g} value={g}>
                                        {g}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                            <label className="filter-label">
                                <FaPhoneAlt className="ms-1 text-primary" />
                                هاتف المشتكي
                            </label>
                            <input
                                type="tel"
                                className={`form-control ${phoneFilter.length > 0 && phoneFilter.length < 10 ? "phone-input-pending" : ""}`}
                                placeholder="09xxxxxxxx"
                                inputMode="numeric"
                                maxLength={10}
                                dir="ltr"
                                value={phoneFilter}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                            />
                            {phoneFilter.length > 0 && phoneFilter.length < 10 && (
                                <div className="phone-hint text-muted x-small mt-1">
                                    أدخل {10 - phoneFilter.length} أرقام إضافية لتفعيل البحث
                                </div>
                            )}
                        </div>

                        <div className="col-6 col-md-4 col-lg-2">
                            <label className="filter-label">
                                <FaCalendarDay className="ms-1 text-info" />
                                من تاريخ
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                value={fromDate}
                                max={toDate || undefined}
                                onChange={(e) => {
                                    setFromDate(e.target.value);
                                    resetPage();
                                }}
                            />
                        </div>

                        <div className="col-6 col-md-4 col-lg-2">
                            <label className="filter-label">
                                <FaCalendarDay className="ms-1 text-info" />
                                إلى تاريخ
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                value={toDate}
                                min={fromDate || undefined}
                                onChange={(e) => {
                                    setToDate(e.target.value);
                                    resetPage();
                                }}
                            />
                        </div>

                        <div className="col-12 col-md-8 col-lg-2">
                            <label className="filter-label">
                                <FaSort className="ms-1 text-secondary" />
                                الترتيب
                            </label>
                            <select
                                className="form-select"
                                value={sortValue}
                                onChange={(e) => {
                                    setSortValue(e.target.value);
                                    resetPage();
                                }}
                            >
                                {SORT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-grow text-primary" />
                    </div>
                ) : (
                    <>
                        <div className="table-responsive d-none d-lg-block">
                            <table className="table custom-modern-table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: "50px" }}>#</th>
                                        <th style={{ width: "190px" }}>المنشأة</th>
                                        <th>مضمون الشكوى</th>
                                        <th style={{ width: "120px" }}>الهاتف</th>
                                        <th style={{ width: "130px" }}>المهام</th>
                                        <th style={{ width: "130px" }}>المعالِج</th>
                                        <th style={{ width: "170px" }}>التواريخ</th>
                                        <th className="text-center" style={{ width: "90px" }}>الحالة</th>
                                        <th className="text-center" style={{ width: "70px" }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {complaints.map((c, index) => (
                                        <tr
                                            key={c.complaintUid}
                                            onClick={() => navigate(`/admin/complaint-details?uid=${c.complaintUid}`)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <td className="text-center fw-bold text-muted">
                                                {(currentPage - 1) * pageSize + index + 1}
                                            </td>
                                            <td>
                                                <div className="fw-bold text-dark small">
                                                    <FaStore className="ms-1 text-primary" size={13} />
                                                    {c.establishment?.name || "—"}
                                                </div>
                                                {c.establishment?.governorate && (
                                                    <div className="text-muted x-small">
                                                        {c.establishment.governorate}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <p className="m-0 text-dark small line-clamp-2" title={c.complaintText}>
                                                    {truncateWords(c.complaintText, 35)}
                                                </p>
                                            </td>
                                            <td>
                                                <span className="phone-inline" dir="ltr">
                                                    {c.submitterPhone || "—"}
                                                </span>
                                            </td>
                                            <td>
                                                {c.missionsTotal > 0 ? (
                                                    <div className="small">
                                                        <span className="badge bg-light text-dark border">
                                                            <FaTasks className="ms-1" />
                                                            {c.missionsTotal}
                                                        </span>
                                                        {c.hasPendingMissions && (
                                                            <span className="badge bg-warning-subtle text-warning border border-warning ms-1">
                                                                معلّقة
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="small fw-bold text-secondary">
                                                    {c.isProcessed ? (
                                                        <>
                                                            <FaUserShield className="ms-1 text-muted" size={11} />
                                                            {c.processedByUsername || "—"}
                                                        </>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small text-dark mb-1">
                                                    <FaCalendarDay size={11} className="ms-1 text-info" />
                                                    {formatDate(c.createdAtUtc)}
                                                </div>
                                                {c.isProcessed && (
                                                    <div className="small text-success fw-bold">
                                                        <FaCheckCircle size={11} className="ms-1" />
                                                        {formatDate(c.processedAtUtc)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <span
                                                    className={`status-chip ${c.isProcessed ? "done" : "pending"}`}
                                                >
                                                    {c.isProcessed ? "معالجة" : "معلّقة"}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light rounded-circle shadow-sm"
                                                >
                                                    <FaChevronLeft size={10} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-lg-none d-flex flex-column gap-3">
                            {complaints.map((c, index) => (
                                <div
                                    key={c.complaintUid}
                                    className="mobile-card"
                                    onClick={() => navigate(`/admin/complaint-details?uid=${c.complaintUid}`)}
                                >
                                    <div className="card-top-section d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span className="card-index">
                                                #{(currentPage - 1) * pageSize + index + 1}
                                            </span>
                                            <div className="fw-bold text-dark small">
                                                <FaStore className="ms-1 text-primary" />
                                                {c.establishment?.name}
                                            </div>
                                        </div>
                                        <span className={`status-chip ${c.isProcessed ? "done" : "pending"}`}>
                                            {c.isProcessed ? "معالجة" : "معلّقة"}
                                        </span>
                                    </div>
                                    <div className="card-content-box my-3">
                                        <p className="m-0 text-dark-emphasis line-clamp-2">
                                            {truncateWords(c.complaintText, 40)}
                                        </p>
                                    </div>
                                    <div className="card-footer-info d-flex justify-content-between align-items-end pt-2 flex-wrap gap-2">
                                        <div className="d-flex flex-column gap-1">
                                            <span className="phone-pill">
                                                <FaPhoneAlt className="ms-1" size={10} />
                                                {c.submitterPhone}
                                            </span>
                                            <span className="date-text">
                                                <FaCalendarDay className="ms-1 text-info" />
                                                {formatDate(c.createdAtUtc)}
                                            </span>
                                            {c.isProcessed && (
                                                <span className="date-text text-success fw-bold">
                                                    <FaCheckCircle className="ms-1" />
                                                    {formatDate(c.processedAtUtc)}
                                                </span>
                                            )}
                                            {c.isProcessed && c.processedByUsername && (
                                                <span className="date-text text-secondary small">
                                                    <FaUserShield className="ms-1" />
                                                    {c.processedByUsername}
                                                </span>
                                            )}
                                        </div>
                                        {c.missionsTotal > 0 && (
                                            <span className="badge bg-light text-dark border">
                                                <FaTasks className="ms-1" />
                                                {c.missionsTotal} مهمة
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {complaints.length === 0 && (
                            <div className="text-center py-5 bg-white rounded-4 shadow-sm border">
                                <FaClipboardList size={32} className="text-muted mb-2 opacity-50" />
                                <p className="text-muted mb-0">لا توجد شكاوى مطابقة للفلاتر الحالية</p>
                            </div>
                        )}

                        {totalCount > 0 && (
                            <div className="pagination-container d-flex flex-column gap-3 mt-4 bg-white p-3 rounded-4 shadow-sm border">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
                                    <div className="text-muted small">
                                        عرض <strong>{complaints.length}</strong> من <strong>{totalCount}</strong>
                                        {governorateFilter && (
                                            <span className="ms-2 badge bg-primary-subtle text-primary">
                                                {governorateFilter}
                                            </span>
                                        )}
                                    </div>
                                    <select
                                        className="form-select form-select-sm w-auto border-0 bg-light rounded-pill px-3"
                                        value={pageSize}
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10 صفوف</option>
                                        <option value={20}>20 صفوف</option>
                                        <option value={50}>50 صفوف</option>
                                        <option value={100}>100 صفوف</option>
                                        <option value={200}>200 صفوف</option>
                                    </select>
                                </div>

                                {totalPages > 1 && (
                                    <div className="d-flex flex-column flex-lg-row align-items-center justify-content-between gap-3">
                                        <ul className="pagination pagination-sm mb-0 gap-1 flex-wrap justify-content-center complaints-pagination">
                                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                <button
                                                    type="button"
                                                    className="page-link rounded-circle border-0 shadow-sm"
                                                    title="الصفحة الأولى"
                                                    onClick={() => goToPage(1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    <FaAngleDoubleRight size={11} />
                                                </button>
                                            </li>
                                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                <button
                                                    type="button"
                                                    className="page-link rounded-circle border-0 shadow-sm"
                                                    title="السابق"
                                                    onClick={() => goToPage(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    <FaChevronRight size={11} />
                                                </button>
                                            </li>
                                            {paginationItems.map((item, idx) =>
                                                item === "ellipsis" ? (
                                                    <li key={`ellipsis-${idx}`} className="page-item disabled">
                                                        <span className="page-link border-0 bg-transparent px-1">…</span>
                                                    </li>
                                                ) : (
                                                    <li
                                                        key={item}
                                                        className={`page-item ${currentPage === item ? "active" : ""}`}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="page-link rounded-circle border-0 shadow-sm"
                                                            onClick={() => goToPage(item)}
                                                        >
                                                            {item}
                                                        </button>
                                                    </li>
                                                )
                                            )}
                                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                <button
                                                    type="button"
                                                    className="page-link rounded-circle border-0 shadow-sm"
                                                    title="التالي"
                                                    onClick={() => goToPage(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    <FaChevronLeft size={11} />
                                                </button>
                                            </li>
                                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                <button
                                                    type="button"
                                                    className="page-link rounded-circle border-0 shadow-sm"
                                                    title="الصفحة الأخيرة"
                                                    onClick={() => goToPage(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    <FaAngleDoubleLeft size={11} />
                                                </button>
                                            </li>
                                        </ul>

                                        <form
                                            className="d-flex align-items-center gap-2 jump-to-page-form"
                                            onSubmit={handleJumpToPage}
                                        >
                                            <label className="small text-muted mb-0 fw-semibold">انتقل إلى</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={totalPages}
                                                className="form-control form-control-sm text-center"
                                                style={{ width: "4.5rem" }}
                                                placeholder={String(currentPage)}
                                                value={jumpToPage}
                                                onChange={(e) => setJumpToPage(e.target.value)}
                                            />
                                            <span className="small text-muted">من {totalPages}</span>
                                            <button type="submit" className="btn btn-sm btn-primary rounded-pill px-3 fw-bold">
                                                انتقال
                                            </button>
                                        </form>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            <style>{`
                .complaints-wrapper { font-family: 'Cairo', sans-serif; }
                .filter-label {
                    display: block;
                    font-size: 0.78rem;
                    font-weight: 700;
                    color: #64748b;
                    margin-bottom: 0.35rem;
                }
                .phone-input-pending { border-color: #ffc107; }
                .phone-hint { font-weight: 600; }
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    line-height: 1.5;
                }
                .registry-top-bar { min-height: 3.25rem; }
                .header-status-tabs {
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    border-radius: 999px;
                    padding: 0.2rem;
                }
                .header-status-btn {
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-weight: 700;
                    font-size: 0.72rem;
                    padding: 0.28rem 0.55rem;
                    border-radius: 999px;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.28rem;
                    white-space: nowrap;
                    transition: all 0.2s ease;
                }
                .header-status-btn em {
                    font-style: normal;
                    font-size: 0.65rem;
                    background: rgba(0,0,0,0.06);
                    padding: 0.05rem 0.35rem;
                    border-radius: 999px;
                    min-width: 1.35rem;
                    text-align: center;
                }
                .header-status-btn:hover { background: rgba(255,255,255,0.75); color: #334155; }
                .header-status-btn.active-all { background: #0d6efd; color: #fff; box-shadow: 0 2px 8px rgba(13, 110, 253, 0.25); }
                .header-status-btn.active-unprocessed { background: #dc3545; color: #fff; box-shadow: 0 2px 8px rgba(220, 53, 69, 0.25); }
                .header-status-btn.active-processed { background: #198754; color: #fff; box-shadow: 0 2px 8px rgba(25, 135, 84, 0.25); }
                .header-status-btn.active-all em,
                .header-status-btn.active-unprocessed em,
                .header-status-btn.active-processed em { background: rgba(255,255,255,0.22); }
                @media (max-width: 575.98px) {
                    .header-status-tabs { width: 100%; justify-content: space-between; }
                    .header-status-btn { flex: 1; justify-content: center; padding-inline: 0.35rem; }
                }
                .status-chip {
                    font-size: 0.72rem;
                    font-weight: 800;
                    padding: 0.3rem 0.65rem;
                    border-radius: 999px;
                    white-space: nowrap;
                }
                .status-chip.pending { background: #fff3cd; color: #856404; }
                .status-chip.done { background: #e6f4ea; color: #137333; }
                .phone-inline { font-weight: 700; color: #0d6efd; font-size: 0.82rem; }
                .custom-modern-table { border-collapse: separate !important; border-spacing: 0 10px !important; width: 100%; }
                .custom-modern-table tbody tr { background: white; transition: 0.2s; }
                .custom-modern-table tbody tr:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(0,0,0,0.05); }
                .custom-modern-table td { padding: 12px 15px; vertical-align: middle; border: none; }
                .custom-modern-table td:first-child { border-top-right-radius: 15px; border-bottom-right-radius: 15px; }
                .custom-modern-table td:last-child { border-top-left-radius: 15px; border-bottom-left-radius: 15px; }
                .mobile-card {
                    background: white; padding: 16px; border-radius: 20px; border: 1px solid rgba(0,0,0,0.05);
                    box-shadow: 0 4px 15px rgba(0,0,0,0.03); transition: 0.2s;
                }
                .card-content-box { background: #f9f9f9; padding: 12px; border-radius: 15px; font-size: 0.88rem; border-right: 3px solid #dee2e6; }
                .phone-pill { color: #0d6efd; font-weight: 800; font-size: 0.8rem; background: rgba(13, 110, 253, 0.08); padding: 3px 10px; border-radius: 50px; display: inline-block; }
                .date-text { color: #adb5bd; font-size: 0.75rem; font-weight: 600; display: block; }
                .x-small { font-size: 0.72rem; }
                .bg-warning-subtle { background-color: #fff3cd !important; }
                .complaints-pagination .page-link {
                    width: 2.1rem; height: 2.1rem; display: flex; align-items: center; justify-content: center;
                    padding: 0; font-weight: 700; font-size: 0.82rem; color: #334155; background: #f8fafc;
                }
                .complaints-pagination .page-item.active .page-link { background: #0d6efd; color: #fff; }
                .complaints-pagination .page-item.disabled .page-link { opacity: 0.45; }
                .jump-to-page-form input::-webkit-outer-spin-button,
                .jump-to-page-form input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
            `}</style>
        </div>
    );
}
